import { useEffect, useRef } from 'react';
import { levelsForRegion, regionForPlanet, type SaveBlob } from '@playnets/data';
import { useUi } from '../store';

/**
 * The map. A single winding path of nodes climbing the plate, the way a
 * casual puzzle game does it — you scroll up through where you've been toward
 * the one node that's open, and the shape of the journey is the progress bar.
 *
 * It scrolls to the current node on open, so the thing you can actually do is
 * always the thing you're looking at.
 */
const GLYPH: Record<string, string> = {
  standard: '',
  elite: '◈',
  boss: '★',
  cache: '◆',
};

export default function SystemMap({
  save,
  onStart,
}: {
  save: SaveBlob;
  onStart: () => void;
}): JSX.Element {
  const levelId = useUi((s) => s.levelId);
  const planetId = useUi((s) => s.planetId);
  const region = regionForPlanet(planetId);
  const levels = levelsForRegion(region.id);
  const currentRef = useRef<HTMLButtonElement | null>(null);

  const isOpen = (id: string): boolean => {
    if (id === levels[0]?.id) return true;
    return levels.some((l) => l.next.includes(id) && (save.stars[l.id] ?? 0) > 0);
  };

  /** The furthest node that's open — where the player is actually up to. */
  const frontier = [...levels].reverse().find((l) => isOpen(l.id)) ?? levels[0];

  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div className="menu menu--map">
      <header className="map-head">
        <div>
          <p className="menu-eyebrow">{region.name}</p>
          <h1 className="map-title">{levels.filter((l) => save.stars[l.id]).length}/{levels.length}</h1>
        </div>
        <button className="jewel-chip" onClick={() => useUi.getState().setScreen('armoury')}>
          <span className="jewel" />
          {save.jewels}
        </button>
      </header>

      {/* Bottom-to-top: the newest node is at the top of the path. */}
      <div className="path">
        {[...levels].reverse().map((l, i) => {
          const open = isOpen(l.id);
          const stars = save.stars[l.id] ?? 0;
          const index = levels.length - i;
          const isCurrent = l.id === frontier?.id;
          // Alternating lean gives the path its snake without any layout maths.
          const lean = (index % 4) - 1.5;
          return (
            <button
              key={l.id}
              ref={isCurrent ? currentRef : undefined}
              className={`pip${open ? '' : ' is-locked'}${l.id === levelId ? ' is-picked' : ''}${
                isCurrent ? ' is-current' : ''
              } pip--${l.kind}`}
              style={{ marginLeft: `${lean * 26}px` }}
              disabled={!open}
              onClick={() => {
                useUi.getState().setLevel(l.id);
                onStart();
              }}
              aria-label={`${l.name}${open ? '' : ' (locked)'}`}
            >
              <span className="pip-index">{GLYPH[l.kind] || index}</span>
              {stars > 0 && (
                <span className="pip-stars">
                  {[0, 1, 2].map((n) => (
                    <i key={n} className={n < stars ? 'is-on' : ''} />
                  ))}
                </span>
              )}
              {isCurrent && open && <span className="pip-name">{l.name}</span>}
            </button>
          );
        })}
      </div>

      <div className="map-foot">
        <button className="ghost" onClick={() => useUi.getState().setScreen('select')}>
          Planets
        </button>
        <button className="ghost" onClick={() => useUi.getState().setScreen('armoury')}>
          Armoury
        </button>
      </div>
    </div>
  );
}
